import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';

/** Where the paper comes from: two cannons at the bottom corners, or a shower from above. */
export type ConfettiOrigin = 'corners' | 'top';

/** One-off overrides for a single {@link ConfettiComponent.fire} call. */
export interface ConfettiBurstOptions {
  particles?: number;
  colors?: string[];
  origin?: ConfettiOrigin;
  /** Milliseconds to wait before the first particle moves. */
  delay?: number;
}

/** Brand palette: primary, its light variant, success, and three accents for contrast. */
export const CONFETTI_COLORS: readonly string[] = [
  'rgb(84,88,234)',
  'rgb(130,134,226)',
  'rgb(0,227,150)',
  'rgb(245,83,155)',
  'rgb(255,184,0)',
  'rgb(170,174,255)',
];

/** Reference stage the physics constants below were tuned on (see `speedScale`). */
const REFERENCE_DIAGONAL = Math.hypot(1920, 1010);

/** Keyframes per particle. Enough for the arc to read as a curve instead of a polyline. */
const STEPS = 40;

/**
 * Confetti, on its own and reusable.
 *
 * Knows nothing about what is being celebrated: it is a box that throws paper when someone calls
 * {@link fire}. Drop it inside any `position: relative` container (it covers it and never takes a
 * pointer event) and hold a reference to it:
 *
 * ```html
 * <app-confetti #confetti></app-confetti>
 * ```
 * ```ts
 * @ViewChild('confetti') confetti?: ConfettiComponent;
 * this.confetti?.fire();
 * ```
 *
 * The particles are plain divs animated with the Web Animations API — each one gets the whole arc
 * precomputed (initial speed, gravity, drag, spin and a sway while it falls), which is what makes
 * the flight believable without pulling in a confetti library. Everything runs outside the Angular
 * zone: a few hundred animations must not wake change detection up a few hundred times.
 *
 * A system asking for less motion gets none: `fire()` returns without doing anything.
 */
@Component({
  selector: 'app-confetti',
  templateUrl: './confetti.component.html',
  styleUrls: ['./confetti.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfettiComponent implements AfterViewInit, OnDestroy {
  /** Particles per cannon (`corners` fires two), or in total when the origin is `top`. */
  @Input() particles = 90;

  @Input() colors: readonly string[] = CONFETTI_COLORS;

  @Input() origin: ConfettiOrigin = 'corners';

  /** Fires as soon as it is created, for the `*ngIf`-and-forget case. */
  @Input() autoplay = false;

  /** The animations in flight, so `clear()` can cancel them instead of waiting them out. */
  private animations: Animation[] = [];
  private pendingFrame?: number;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    if (!this.autoplay) return;
    // One frame later: `fire()` measures the box, and when the component is created by an `*ngIf`
    // inside an overlay that is still animating in, it has no size yet.
    this.zone.runOutsideAngular(() => {
      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = undefined;
        this.fire();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.pendingFrame !== undefined) cancelAnimationFrame(this.pendingFrame);
    this.clear();
  }

  /** Throws one burst. Clears whatever was still flying first, so two calls never pile up. */
  fire(options: ConfettiBurstOptions = {}): void {
    if (prefersReducedMotion()) return;

    const box = this.host.nativeElement;
    const width = box.clientWidth;
    const height = box.clientHeight;
    if (!width || !height) return;

    this.clear();

    const count = options.particles ?? this.particles;
    const colors = options.colors ?? this.colors;
    const origin = options.origin ?? this.origin;
    const delay = options.delay ?? 0;
    // The constants were tuned on a 1920×1010 stage: scaling them by the box's own diagonal keeps
    // the same throw on a phone and on a desktop instead of shooting the paper off-screen.
    const speedScale = Math.hypot(width, height) / REFERENCE_DIAGONAL;

    this.zone.runOutsideAngular(() => {
      if (origin === 'top') {
        this.shower(box, width, count, colors, speedScale, delay);
        return;
      }
      // Two cannons from the bottom corners, aimed inwards at 60° — the reference's own angles.
      this.cannon(box, 0, height, -Math.PI / 3, count, colors, speedScale, delay);
      this.cannon(box, width, height, (-2 * Math.PI) / 3, count, colors, speedScale, delay);
    });
  }

  /** Cancels what is in flight and empties the box. */
  clear(): void {
    this.animations.forEach((animation) => animation.cancel());
    this.animations = [];
    this.host.nativeElement.replaceChildren();
  }

  // -------------------------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------------------------

  /** A burst thrown from one point towards `direction` (radians, screen coordinates). */
  private cannon(
    box: HTMLElement,
    cx: number,
    cy: number,
    direction: number,
    count: number,
    colors: readonly string[],
    speedScale: number,
    delay: number
  ): void {
    for (let i = 0; i < count; i++) {
      const angle = direction + (Math.random() - 0.5) * 0.6;
      const speed = (2600 + Math.random() * 1200) * speedScale;
      this.particle(box, cx, cy, Math.cos(angle) * speed, Math.sin(angle) * speed, {
        color: colors[i % colors.length],
        speedScale,
        // A ragged start (biased towards the beginning) is what reads as a cannon rather than as a
        // single sheet of paper leaving at once.
        delay: delay + Math.pow(Math.random(), 1.5) * 500,
      });
    }
  }

  /** Paper falling in from above the box, spread across its width. */
  private shower(
    box: HTMLElement,
    width: number,
    count: number,
    colors: readonly string[],
    speedScale: number,
    delay: number
  ): void {
    for (let i = 0; i < count; i++) {
      const vx = (Math.random() - 0.5) * 400 * speedScale;
      this.particle(box, Math.random() * width, -24, vx, 120 * speedScale, {
        color: colors[i % colors.length],
        speedScale,
        delay: delay + Math.random() * 900,
      });
    }
  }

  /**
   * One piece of paper: a div whose whole flight is integrated up front into keyframes.
   *
   * Gravity pulls it down, drag eats the initial speed (harder on the way down, so it settles into a
   * fall), a spin turns it on both axes and, once it is falling, a sway makes it drift side to side.
   */
  private particle(
    box: HTMLElement,
    cx: number,
    cy: number,
    vx0: number,
    vy0: number,
    config: { color: string; speedScale: number; delay: number }
  ): void {
    const paper = document.createElement('div');
    const width = 6 + Math.random() * 8;
    const height = width * (1.2 + Math.random());
    paper.className = 'confetti-piece';
    paper.style.left = `${cx}px`;
    paper.style.top = `${cy}px`;
    paper.style.width = `${width}px`;
    paper.style.height = `${height}px`;
    paper.style.background = config.color;
    box.appendChild(paper);

    const gravity = 1500 * config.speedScale;
    const drag = 1.6;
    const spin = (Math.random() - 0.5) * 1400;
    const sway = (40 + Math.random() * 60) * config.speedScale;
    const phase = Math.random() * 6;
    const seconds = 2.25 + Math.random() * 0.75;
    const dt = seconds / STEPS;

    let vx = vx0;
    let vy = vy0;
    let x = 0;
    let y = 0;
    const keyframes: Keyframe[] = [];

    for (let step = 0; step <= STEPS; step++) {
      const t = step * dt;
      const drift = vy > 0 ? Math.sin(t * 5 + phase) * sway * Math.min(1, t) : 0;
      keyframes.push({
        transform:
          `translate(${x + drift}px, ${y}px) translate(-50%, -50%) ` +
          `rotate(${spin * t}deg) rotateX(${t * 540}deg)`,
        // Fades out over the last 15% of the flight instead of blinking off.
        opacity: step > STEPS * 0.85 ? (STEPS - step) / (STEPS * 0.15) : 1,
      });
      vx -= vx * drag * dt;
      vy += gravity * dt - vy * drag * dt * (vy < 0 ? 1 : 2.2);
      x += vx * dt;
      y += vy * dt;
    }

    const animation = paper.animate(keyframes, {
      delay: config.delay,
      duration: seconds * 1000,
      easing: 'linear',
      fill: 'both',
    });
    animation.onfinish = () => paper.remove();
    this.animations.push(animation);
  }
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}
